/**
 * SHOPKO — Galería principal de producto en móvil: swipe horizontal +
 * scroll vertical natural, con "axis lock" real. v5.
 *
 * Ver assets/shopko-gallery-touch-fix.css para el historial de v1-v4 y
 * por qué se descartaron. Resumen del mecanismo de esta versión:
 *
 * El CSS ya deja el visor principal (<ul class="product__media-list
 * slider"> dentro de <media-gallery><slider-component>) sin superficie
 * de scroll horizontal NATIVA en móvil ("overflow-x: hidden" permanente
 * + "scroll-snap-type: none"). Eso significa que, pase lo que pase en
 * este script, el navegador NUNCA intenta capturar el gesto para scroll
 * horizontal — el scroll vertical de la página queda siempre disponible
 * de forma nativa e inmediata. Este script NUNCA llama a
 * preventDefault() y todos sus listeners son passive: true.
 *
 * Lo que este script SÍ hace es decidir, con un "axis lock" clásico, si
 * un gesto que empieza sobre la imagen debe mover la galería:
 *
 *   1. touchstart: se guarda la posición inicial. No se decide nada.
 *   2. touchmove: mientras el desplazamiento acumulado sea menor a
 *      DECISION_THRESHOLD_PX en ambos ejes, se espera (zona ambigua).
 *   3. Superado el umbral, se clasifica UNA sola vez:
 *        - horizontal solo si abs(dx) > abs(dy) * AXIS_RATIO
 *        - en cualquier otro caso (incluida la ambigüedad), vertical.
 *      Esa clasificación queda "bloqueada" hasta soltar el dedo.
 *   4. Si quedó clasificado VERTICAL: el script no vuelve a tocar ese
 *      gesto para nada — ni transform, ni scrollLeft, ni preventDefault.
 *      El scroll de la página sigue su curso nativo sin ninguna
 *      interferencia.
 *   5. Si quedó clasificado HORIZONTAL: recién ahí el script empieza a
 *      mover scrollLeft del propio visor siguiendo al dedo 1 a 1 (con
 *      requestAnimationFrame para que sea fluido), con una pequeña
 *      resistencia en los extremos. Los navegadores modernos (incluido
 *      Safari/iOS) permiten mover el contenido de un elemento con
 *      "overflow-x:hidden" vía scrollLeft/scrollTo aunque el usuario no
 *      pueda arrastrarlo de forma nativa — por eso esto funciona igual
 *      que si el visor siguiera siendo "auto", pero sin la ambigüedad
 *      nativa que causaba los enganches en v2/v3.
 *   6. Al soltar el dedo (touchend/touchcancel) durante un gesto
 *      horizontal, se hace "snap" a la imagen más cercana con
 *      scrollTo({behavior:'smooth'}) — el mismo mecanismo nativo del
 *      tema que ya usan las flechas ‹ › y los dots (sin modificar).
 *
 * No hay overflow que se active/desactive por evento, no hay timeouts
 * de seguridad, no hay clicks programáticos en botones.
 *
 * ALCANCE: SOLO el visor principal, SOLO en viewport móvil
 * (max-width: 749px). El carrusel de miniaturas no se toca. Desktop no
 * se toca (no hay eventos "touch" en desktop). No toca checkout,
 * carrito, Starken, pixel, pagos ni apps.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var DECISION_THRESHOLD_PX = 10; // px antes de clasificar el eje del gesto
  var AXIS_RATIO = 1.6; // dx debe superar a dy por este factor para ser "horizontal"
  var EDGE_RESISTANCE = 3; // divisor de resistencia al arrastrar más allá de los bordes

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function initSlider(ul) {
    if (!ul || ul.__shopkoAxisLockInit) return;
    ul.__shopkoAxisLockInit = true;

    var gesture = null; // estado del gesto activo, o null si no hay ninguno
    var rafId = null;

    function getSlideWidth() {
      var first = ul.querySelector(':scope > .product__media-item');
      var width = first ? first.getBoundingClientRect().width : ul.clientWidth;
      return width || ul.clientWidth || 1;
    }

    function scheduleApply() {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(function () {
        rafId = null;
        if (!gesture || gesture.axis !== 'x') return;
        var next = gesture.startScrollLeft - (gesture.lastX - gesture.startX);
        var max = gesture.maxScroll;
        if (next < 0) {
          next = next / EDGE_RESISTANCE;
        } else if (next > max) {
          next = max + (next - max) / EDGE_RESISTANCE;
        }
        ul.scrollLeft = next;
      });
    }

    function settle() {
      var width = getSlideWidth();
      var max = ul.scrollWidth - ul.clientWidth;
      var target = Math.round(ul.scrollLeft / width) * width;
      target = Math.max(0, Math.min(target, max));
      if (typeof ul.scrollTo === 'function') {
        ul.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        ul.scrollLeft = target;
      }
    }

    function onTouchStart(e) {
      if (!isMobile()) return;
      if (!e.touches || e.touches.length !== 1) {
        gesture = null;
        return;
      }
      gesture = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        lastX: e.touches[0].clientX,
        axis: null, // null = todavía decidiendo
        startScrollLeft: ul.scrollLeft,
        maxScroll: ul.scrollWidth - ul.clientWidth
      };
    }

    function onTouchMove(e) {
      if (!gesture) return;
      if (!e.touches || e.touches.length !== 1) {
        // gesto multi-touch (ej. pinch-zoom): soltamos el control por completo.
        gesture = null;
        return;
      }

      var x = e.touches[0].clientX;
      var y = e.touches[0].clientY;
      var dx = x - gesture.startX;
      var dy = y - gesture.startY;

      if (gesture.axis === null) {
        if (Math.abs(dx) < DECISION_THRESHOLD_PX && Math.abs(dy) < DECISION_THRESHOLD_PX) {
          return; // zona ambigua: todavía no hay suficiente movimiento para decidir
        }
        gesture.axis = Math.abs(dx) > Math.abs(dy) * AXIS_RATIO ? 'x' : 'y';
      }

      if (gesture.axis === 'y') {
        // Gesto vertical: no se hace absolutamente nada. El scroll nativo
        // de la página ya está ocurriendo (nunca llamamos preventDefault).
        return;
      }

      // Gesto horizontal: seguimos al dedo.
      gesture.lastX = x;
      scheduleApply();
    }

    function onTouchEnd() {
      if (!gesture) return;
      if (gesture.axis === 'x') {
        settle();
      }
      gesture = null;
    }

    ul.addEventListener('touchstart', onTouchStart, { passive: true });
    ul.addEventListener('touchmove', onTouchMove, { passive: true });
    ul.addEventListener('touchend', onTouchEnd, { passive: true });
    ul.addEventListener('touchcancel', onTouchEnd, { passive: true });
  }

  function run() {
    document
      .querySelectorAll('media-gallery slider-component > .product__media-list.slider')
      .forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Soporte para el editor de temas (cambios de variante / sección en vivo).
  document.addEventListener('shopify:section:load', run);
})();
